/**
 * Money arrives from the API as BigFloat strings ("45.50") and is sent back as
 * strings. The apps never compute a bill: these helpers only parse what a user
 * types, compare it with an amount the API returned, and add up amounts for
 * display, in whole centimes so no floating-point error creeps in.
 */

import { toWesternDigits } from './text';

/**
 * "45.5" → 4550, "-33500.00" → -3350000 (a balance can be negative). Throws on
 * anything that isn't a plain amount.
 */
export function toCents(amount: string): number {
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim());
  if (!match) throw new Error(`Not an amount: ${amount}`);
  const cents =
    Number(match[2]) * 100 + Number((match[3] ?? '').padEnd(2, '0'));
  return match[1] ? -cents : cents;
}

/** 4550 → "45.50", -50 → "-0.50": the string the API expects. */
export function fromCents(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents < 0 ? '-' : '';
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Sum of API amounts, for display ("المجموع"). */
export function sumAmounts(amounts: (string | null | undefined)[]): string {
  return fromCents(amounts.reduce((sum, a) => sum + toCents(a ?? '0'), 0));
}

/**
 * What a user typed ("45", "45,5", "٤٥٫٥٠") as an API amount ("45.50"), or
 * null when it isn't a positive amount with at most two decimals.
 */
export function parseAmount(input: string): string | null {
  const normalized = toWesternDigits(input)
    .trim()
    .replace(/[,٫]/g, '.')
    .replace(/\s/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const cents = toCents(normalized);
  return cents > 0 ? fromCents(cents) : null;
}
